import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import { Percent, ShieldCheck, Wallet, Plus, Check, Trash2, Landmark } from "lucide-react";

const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
    if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
    return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

const TABS = [
    { key: "tds", label: "TDS Control", icon: Percent },
    { key: "retention", label: "Retention Money", icon: ShieldCheck },
    { key: "advances", label: "Client Advances", icon: Wallet },
];

export default function TDSRetention() {
    const [tab, setTab] = useState("tds");
    return (
        <div className="space-y-4" data-testid="tds-retention-page">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// PHASE C · CLIENT LEDGERS</div>
                <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">TDS · Retention · Advances</h1>
                <div className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Track statutory deductions · retention money held/released · mobilisation advances
                </div>
            </div>

            <div className="flex gap-1 border-b border-[var(--border)]">
                {TABS.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        data-testid={`tab-${key}`}
                        onClick={() => setTab(key)}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-mono-fin uppercase tracking-wider transition-colors ${
                            tab === key
                                ? "text-[var(--brand-amber)] border-b-2 border-[var(--brand-amber)] -mb-px"
                                : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        }`}
                    >
                        <Icon size={14} /> {label}
                    </button>
                ))}
            </div>

            {tab === "tds" && <TDSTab />}
            {tab === "retention" && <RetentionTab />}
            {tab === "advances" && <AdvancesTab />}
        </div>
    );
}

// ============ TDS TAB ============
function TDSTab() {
    const [data, setData] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [depositTarget, setDepositTarget] = useState(null);
    const [clients, setClients] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [projects, setProjects] = useState([]);

    const reload = async () => {
        const r = await api.get("/tds/entries");
        setData(r.data);
    };
    useEffect(() => {
        reload();
        api.get("/data/clients").then((r) => setClients(r.data.items || []));
        api.get("/data/vendors").then((r) => setVendors(r.data.items || []));
        api.get("/data/projects").then((r) => setProjects(r.data.items || []));
    }, []);

    if (!data) return <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-6">Loading TDS ledger…</div>;
    const s = data.summary || {};

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Total TDS" value={fmt(s.total_tds)} testId="kpi-tds-total" />
                <Kpi label="Pending Deposit" value={fmt(s.by_status?.pending)} tone="warn" testId="kpi-tds-pending" />
                <Kpi label="Deposited" value={fmt(s.by_status?.deposited)} tone="pos" testId="kpi-tds-deposited" />
                <Kpi label="Reconciled" value={fmt(s.by_status?.reconciled)} tone="pos" testId="kpi-tds-recon" />
            </div>

            {(s.by_section || []).length > 0 && (
                <Panel title="Breakdown by Section" subtitle="Section 194C · 194J · 194I · 194Q etc.">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {(s.by_section || []).map((b) => (
                            <div key={b.section} className="p-3 border border-[var(--border)]" data-testid={`tds-section-${b.section}`}>
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Section {b.section}</div>
                                <div className="text-lg font-bold mt-1">{fmt(b.amount)}</div>
                                <div className="text-[10px] text-[var(--text-secondary)]">{b.count} entries</div>
                            </div>
                        ))}
                    </div>
                </Panel>
            )}

            <Panel
                title={`TDS Entries · ${data.count}`}
                right={
                    <button
                        data-testid="btn-add-tds"
                        onClick={() => setShowForm(true)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin text-[11px] uppercase tracking-wider"
                    >
                        <Plus size={12} /> Add TDS Entry
                    </button>
                }
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-[var(--border)] font-mono-fin text-[10px] uppercase text-[var(--text-secondary)]">
                            <tr>
                                <th className="text-left py-2 px-2">Date</th>
                                <th className="text-left px-2">Party</th>
                                <th className="text-left px-2">Section</th>
                                <th className="text-right px-2">Base Amt</th>
                                <th className="text-right px-2">%</th>
                                <th className="text-right px-2">TDS Amt</th>
                                <th className="text-center px-2">Status</th>
                                <th className="text-left px-2">Challan</th>
                                <th className="text-right px-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.entries || []).length === 0 && (
                                <tr>
                                    <td colSpan={9} className="text-center py-8 text-[var(--text-secondary)] text-sm">
                                        No TDS entries yet — click "Add TDS Entry" to record your first one.
                                    </td>
                                </tr>
                            )}
                            {(data.entries || []).map((e) => (
                                <tr key={e.id} className="border-b border-[var(--border)]" data-testid={`tds-row-${e.id}`}>
                                    <td className="py-2 px-2 text-xs">{e.date}</td>
                                    <td className="px-2 text-xs">
                                        <div>{e.party_name}</div>
                                        <div className="text-[10px] text-[var(--text-secondary)]">{e.party_type}</div>
                                    </td>
                                    <td className="px-2 text-xs font-mono-fin">{e.tds_section}</td>
                                    <td className="px-2 text-right text-xs">{fmt(e.base_amount)}</td>
                                    <td className="px-2 text-right text-xs">{e.tds_pct}%</td>
                                    <td className="px-2 text-right text-xs font-bold">{fmt(e.tds_amount)}</td>
                                    <td className="px-2 text-center">
                                        <StatusPill status={e.status} />
                                    </td>
                                    <td className="px-2 text-[10px] font-mono-fin">{e.challan_no || "—"}</td>
                                    <td className="px-2 text-right">
                                        {e.status === "pending" && (
                                            <button
                                                data-testid={`btn-deposit-${e.id}`}
                                                onClick={() => setDepositTarget(e)}
                                                className="text-[10px] font-mono-fin text-[var(--brand-amber)] uppercase"
                                            >
                                                Deposit →
                                            </button>
                                        )}
                                        <button
                                            data-testid={`btn-delete-tds-${e.id}`}
                                            onClick={async () => {
                                                if (!window.confirm(`Delete TDS entry ${e.id}?`)) return;
                                                await api.delete(`/tds/entries/${e.id}`);
                                                toast.success("TDS entry deleted");
                                                reload();
                                            }}
                                            className="ml-2 text-[var(--neg)]"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            {showForm && (
                <TDSForm
                    onClose={() => setShowForm(false)}
                    onSaved={() => {
                        setShowForm(false);
                        reload();
                    }}
                    clients={clients}
                    vendors={vendors}
                    projects={projects}
                />
            )}

            {depositTarget && (
                <DepositTDSModal
                    entry={depositTarget}
                    onClose={() => setDepositTarget(null)}
                    onDone={() => {
                        setDepositTarget(null);
                        reload();
                    }}
                />
            )}
        </div>
    );
}

function TDSForm({ onClose, onSaved, clients, vendors, projects }) {
    const [f, setF] = useState({
        party_type: "client",
        party_id: "",
        project_id: "",
        ra_bill_no: "",
        date: new Date().toISOString().slice(0, 10),
        base_amount: "",
        tds_section: "194C",
        tds_pct: 2,
        tds_amount: "",
        status: "pending",
        notes: "",
    });

    const parties = f.party_type === "client" ? clients : vendors;

    // Auto-calc TDS amount when base × pct
    useEffect(() => {
        if (f.base_amount && f.tds_pct !== "") {
            const amt = Number(f.base_amount) * Number(f.tds_pct) / 100;
            setF((prev) => ({ ...prev, tds_amount: amt.toFixed(2) }));
        }
    }, [f.base_amount, f.tds_pct]);

    const submit = async () => {
        if (!f.party_id) return toast.error("Select a party");
        if (!f.base_amount) return toast.error("Enter base amount");
        if (!f.tds_amount) return toast.error("Enter TDS amount");
        try {
            await api.post("/tds/entries", {
                ...f,
                base_amount: Number(f.base_amount),
                tds_pct: Number(f.tds_pct),
                tds_amount: Number(f.tds_amount),
                project_id: f.project_id || null,
                ra_bill_no: f.ra_bill_no || null,
            });
            toast.success("TDS entry created");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        }
    };

    return (
        <ModalShell title="Add TDS Entry" onClose={onClose} testId="modal-add-tds">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Party Type">
                    <select
                        data-testid="input-tds-party-type"
                        value={f.party_type}
                        onChange={(e) => setF({ ...f, party_type: e.target.value, party_id: "" })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    >
                        <option value="client">Client deducted TDS from our bill</option>
                        <option value="vendor">We deducted TDS on vendor invoice</option>
                    </select>
                </Field>
                <Field label={f.party_type === "client" ? "Client" : "Vendor"}>
                    <select
                        data-testid="input-tds-party-id"
                        value={f.party_id}
                        onChange={(e) => setF({ ...f, party_id: e.target.value })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    >
                        <option value="">-- select --</option>
                        {parties.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Project (optional)">
                    <select
                        data-testid="input-tds-project"
                        value={f.project_id}
                        onChange={(e) => setF({ ...f, project_id: e.target.value })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    >
                        <option value="">--</option>
                        {projects.filter((p) => !f.party_id || p.client_id === f.party_id || f.party_type === "vendor").map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Date">
                    <input
                        data-testid="input-tds-date"
                        type="date"
                        value={f.date}
                        onChange={(e) => setF({ ...f, date: e.target.value })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    />
                </Field>
                <Field label="RA Bill / Invoice No.">
                    <input
                        data-testid="input-tds-ra-bill"
                        value={f.ra_bill_no}
                        onChange={(e) => setF({ ...f, ra_bill_no: e.target.value })}
                        placeholder="e.g. RA-01 / INV-234"
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    />
                </Field>
                <Field label="Section">
                    <select
                        data-testid="input-tds-section"
                        value={f.tds_section}
                        onChange={(e) => {
                            const defaultPct = { "194C": 2, "194J": 10, "194I": 10, "194Q": 0.1, "194H": 5 }[e.target.value] ?? f.tds_pct;
                            setF({ ...f, tds_section: e.target.value, tds_pct: defaultPct });
                        }}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    >
                        <option value="194C">194C · Contractor (2%)</option>
                        <option value="194J">194J · Professional (10%)</option>
                        <option value="194I">194I · Rent (10%)</option>
                        <option value="194Q">194Q · Goods Purchase (0.1%)</option>
                        <option value="194H">194H · Commission (5%)</option>
                        <option value="194IA">194IA · Property Transfer</option>
                        <option value="194IB">194IB · Rent by Individual</option>
                        <option value="194IC">194IC · JD Agreement</option>
                        <option value="other">Other</option>
                    </select>
                </Field>
                <Field label="Base Amount (₹)">
                    <input
                        data-testid="input-tds-base"
                        type="number"
                        value={f.base_amount}
                        onChange={(e) => setF({ ...f, base_amount: e.target.value })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    />
                </Field>
                <Field label="TDS %">
                    <input
                        data-testid="input-tds-pct"
                        type="number"
                        step="0.01"
                        value={f.tds_pct}
                        onChange={(e) => setF({ ...f, tds_pct: e.target.value })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    />
                </Field>
                <Field label="TDS Amount (₹)">
                    <input
                        data-testid="input-tds-amount"
                        type="number"
                        value={f.tds_amount}
                        onChange={(e) => setF({ ...f, tds_amount: e.target.value })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-bold"
                    />
                </Field>
                <Field label="Notes" className="col-span-2">
                    <input
                        data-testid="input-tds-notes"
                        value={f.notes}
                        onChange={(e) => setF({ ...f, notes: e.target.value })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    />
                </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <button data-testid="btn-cancel-tds" onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">
                    Cancel
                </button>
                <button data-testid="btn-save-tds" onClick={submit} className="px-4 py-1.5 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold">
                    Save TDS Entry
                </button>
            </div>
        </ModalShell>
    );
}

function DepositTDSModal({ entry, onClose, onDone }) {
    const [challan, setChallan] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const submit = async () => {
        if (!challan) return toast.error("Challan number required");
        try {
            await api.post(`/tds/entries/${entry.id}/deposit?challan_no=${encodeURIComponent(challan)}&deposit_date=${date}`);
            toast.success("Marked as deposited");
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };
    return (
        <ModalShell title={`Deposit TDS · ${fmt(entry.tds_amount)}`} onClose={onClose} testId="modal-deposit-tds">
            <div className="space-y-3">
                <Field label="Challan No.">
                    <input
                        data-testid="input-deposit-challan"
                        value={challan}
                        onChange={(e) => setChallan(e.target.value)}
                        placeholder="ITNS 281 Challan / BSR code"
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    />
                </Field>
                <Field label="Deposit Date">
                    <input
                        data-testid="input-deposit-date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    />
                </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                <button data-testid="btn-confirm-deposit" onClick={submit} className="px-4 py-1.5 bg-[var(--pos)] text-black text-xs font-mono-fin uppercase font-bold">
                    Confirm Deposit
                </button>
            </div>
        </ModalShell>
    );
}

// ============ RETENTION TAB ============
function RetentionTab() {
    const [data, setData] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [releaseTarget, setReleaseTarget] = useState(null);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);

    const reload = async () => {
        const r = await api.get("/retention/entries");
        setData(r.data);
    };
    useEffect(() => {
        reload();
        api.get("/data/clients").then((r) => setClients(r.data.items || []));
        api.get("/data/projects").then((r) => setProjects(r.data.items || []));
    }, []);

    if (!data) return <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-6">Loading retention ledger…</div>;
    const s = data.summary || {};

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Retention Held" value={fmt(s.total_held)} tone="warn" testId="kpi-ret-held" />
                <Kpi label="Retention Released" value={fmt(s.total_released)} tone="pos" testId="kpi-ret-released" />
                <Kpi label="Due for Release" value={fmt(s.due_for_release)} tone="neg" testId="kpi-ret-due" />
                <Kpi label="Total Entries" value={data.count} testId="kpi-ret-count" />
            </div>

            <Panel
                title={`Retention Entries · ${data.count}`}
                right={
                    <button
                        data-testid="btn-add-retention"
                        onClick={() => setShowForm(true)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin text-[11px] uppercase tracking-wider"
                    >
                        <Plus size={12} /> Add Retention
                    </button>
                }
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-[var(--border)] font-mono-fin text-[10px] uppercase text-[var(--text-secondary)]">
                            <tr>
                                <th className="text-left py-2 px-2">RA Bill</th>
                                <th className="text-left px-2">Client / Project</th>
                                <th className="text-right px-2">Bill Amt</th>
                                <th className="text-right px-2">Ret %</th>
                                <th className="text-right px-2">Retention</th>
                                <th className="text-left px-2">Due Date</th>
                                <th className="text-center px-2">Status</th>
                                <th className="text-right px-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.entries || []).length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-[var(--text-secondary)] text-sm">
                                        No retention entries yet — add one when you raise an RA bill with retention.
                                    </td>
                                </tr>
                            )}
                            {(data.entries || []).map((e) => (
                                <tr key={e.id} className="border-b border-[var(--border)]" data-testid={`ret-row-${e.id}`}>
                                    <td className="py-2 px-2 text-xs font-mono-fin">{e.ra_bill_no}</td>
                                    <td className="px-2 text-xs">
                                        <div>{e.client_name}</div>
                                        <div className="text-[10px] text-[var(--text-secondary)]">{e.project_name}</div>
                                    </td>
                                    <td className="px-2 text-right text-xs">{fmt(e.ra_bill_amount)}</td>
                                    <td className="px-2 text-right text-xs">{e.retention_pct}%</td>
                                    <td className="px-2 text-right text-xs font-bold">{fmt(e.retention_amount)}</td>
                                    <td className="px-2 text-xs">
                                        <div>{e.retention_release_due_date}</div>
                                        {e.days_to_release !== null && e.status === "held" && (
                                            <div className={`text-[10px] ${e.days_to_release < 0 ? "text-[var(--neg)]" : "text-[var(--text-secondary)]"}`}>
                                                {e.days_to_release < 0 ? `${Math.abs(e.days_to_release)}d overdue` : `${e.days_to_release}d to go`}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 text-center">
                                        <StatusPill status={e.release_status || e.status} />
                                    </td>
                                    <td className="px-2 text-right">
                                        {e.status === "held" && (
                                            <button
                                                data-testid={`btn-release-${e.id}`}
                                                onClick={() => setReleaseTarget(e)}
                                                className="text-[10px] font-mono-fin text-[var(--pos)] uppercase"
                                            >
                                                Release →
                                            </button>
                                        )}
                                        <button
                                            data-testid={`btn-delete-ret-${e.id}`}
                                            onClick={async () => {
                                                if (!window.confirm(`Delete retention ${e.id}?`)) return;
                                                await api.delete(`/retention/entries/${e.id}`);
                                                toast.success("Deleted");
                                                reload();
                                            }}
                                            className="ml-2 text-[var(--neg)]"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            {showForm && (
                <RetentionForm
                    onClose={() => setShowForm(false)}
                    onSaved={() => {
                        setShowForm(false);
                        reload();
                    }}
                    clients={clients}
                    projects={projects}
                />
            )}
            {releaseTarget && (
                <ReleaseRetentionModal
                    entry={releaseTarget}
                    onClose={() => setReleaseTarget(null)}
                    onDone={() => {
                        setReleaseTarget(null);
                        reload();
                    }}
                />
            )}
        </div>
    );
}

function RetentionForm({ onClose, onSaved, clients, projects }) {
    const [f, setF] = useState({
        client_id: "",
        project_id: "",
        ra_bill_no: "",
        ra_bill_date: new Date().toISOString().slice(0, 10),
        ra_bill_amount: "",
        retention_pct: 5,
        retention_amount: "",
        defect_liability_months: 12,
        notes: "",
    });

    // Auto-compute retention amount
    useEffect(() => {
        if (f.ra_bill_amount && f.retention_pct !== "") {
            const amt = Number(f.ra_bill_amount) * Number(f.retention_pct) / 100;
            setF((prev) => ({ ...prev, retention_amount: amt.toFixed(2) }));
        }
    }, [f.ra_bill_amount, f.retention_pct]);

    const filteredProjects = projects.filter((p) => !f.client_id || p.client_id === f.client_id);

    const submit = async () => {
        if (!f.client_id || !f.project_id || !f.ra_bill_no || !f.ra_bill_amount) {
            return toast.error("Client, Project, RA Bill No. and Amount required");
        }
        try {
            await api.post("/retention/entries", {
                ...f,
                ra_bill_amount: Number(f.ra_bill_amount),
                retention_pct: Number(f.retention_pct),
                retention_amount: Number(f.retention_amount),
                defect_liability_months: Number(f.defect_liability_months),
            });
            toast.success("Retention entry saved");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    return (
        <ModalShell title="Add Retention Entry" onClose={onClose} testId="modal-add-retention">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Client">
                    <select
                        data-testid="input-ret-client"
                        value={f.client_id}
                        onChange={(e) => setF({ ...f, client_id: e.target.value, project_id: "" })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    >
                        <option value="">-- select --</option>
                        {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                    </select>
                </Field>
                <Field label="Project">
                    <select
                        data-testid="input-ret-project"
                        value={f.project_id}
                        onChange={(e) => setF({ ...f, project_id: e.target.value })}
                        className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"
                    >
                        <option value="">-- select --</option>
                        {filteredProjects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                    </select>
                </Field>
                <Field label="RA Bill No.">
                    <input data-testid="input-ret-ra-bill" value={f.ra_bill_no} onChange={(e) => setF({ ...f, ra_bill_no: e.target.value })} placeholder="RA-01" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="RA Bill Date">
                    <input data-testid="input-ret-date" type="date" value={f.ra_bill_date} onChange={(e) => setF({ ...f, ra_bill_date: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Bill Amount (₹)">
                    <input data-testid="input-ret-amount" type="number" value={f.ra_bill_amount} onChange={(e) => setF({ ...f, ra_bill_amount: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Retention %">
                    <input data-testid="input-ret-pct" type="number" step="0.01" value={f.retention_pct} onChange={(e) => setF({ ...f, retention_pct: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Retention Amount (auto)">
                    <input data-testid="input-ret-computed" type="number" value={f.retention_amount} onChange={(e) => setF({ ...f, retention_amount: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-bold" />
                </Field>
                <Field label="Defect Liability (months)">
                    <input data-testid="input-ret-dlp" type="number" value={f.defect_liability_months} onChange={(e) => setF({ ...f, defect_liability_months: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Notes" className="col-span-2">
                    <input data-testid="input-ret-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                <button data-testid="btn-save-retention" onClick={submit} className="px-4 py-1.5 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold">
                    Save Retention
                </button>
            </div>
        </ModalShell>
    );
}

function ReleaseRetentionModal({ entry, onClose, onDone }) {
    const [f, setF] = useState({
        released_date: new Date().toISOString().slice(0, 10),
        released_amount: entry.retention_amount,
        release_reference: "",
        notes: "",
    });
    const submit = async () => {
        try {
            await api.post(`/retention/entries/${entry.id}/release`, {
                ...f,
                released_amount: Number(f.released_amount),
            });
            toast.success("Retention released");
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };
    return (
        <ModalShell title={`Release Retention · ${entry.ra_bill_no}`} onClose={onClose} testId="modal-release-retention">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Release Date">
                    <input data-testid="input-release-date" type="date" value={f.released_date} onChange={(e) => setF({ ...f, released_date: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Amount Released (₹)">
                    <input data-testid="input-release-amount" type="number" value={f.released_amount} onChange={(e) => setF({ ...f, released_amount: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-bold" />
                </Field>
                <Field label="Reference (UTR / cheque)" className="col-span-2">
                    <input data-testid="input-release-ref" value={f.release_reference} onChange={(e) => setF({ ...f, release_reference: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Notes" className="col-span-2">
                    <input data-testid="input-release-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                <button data-testid="btn-confirm-release" onClick={submit} className="px-4 py-1.5 bg-[var(--pos)] text-black text-xs font-mono-fin uppercase font-bold">
                    Confirm Release
                </button>
            </div>
        </ModalShell>
    );
}

// ============ ADVANCES TAB ============
function AdvancesTab() {
    const [data, setData] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [adjustTarget, setAdjustTarget] = useState(null);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);

    const reload = async () => {
        const r = await api.get("/advances/entries");
        setData(r.data);
    };
    useEffect(() => {
        reload();
        api.get("/data/clients").then((r) => setClients(r.data.items || []));
        api.get("/data/projects").then((r) => setProjects(r.data.items || []));
    }, []);

    if (!data) return <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-6">Loading advances ledger…</div>;
    const s = data.summary || {};

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Total Received" value={fmt(s.total_received)} testId="kpi-adv-received" />
                <Kpi label="Adjusted" value={fmt(s.total_adjusted)} tone="pos" testId="kpi-adv-adjusted" />
                <Kpi label="Outstanding" value={fmt(s.outstanding)} tone="warn" testId="kpi-adv-outstanding" />
                <Kpi label="Total Entries" value={data.count} testId="kpi-adv-count" />
            </div>

            <Panel
                title={`Advance Entries · ${data.count}`}
                right={
                    <button
                        data-testid="btn-add-advance"
                        onClick={() => setShowForm(true)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin text-[11px] uppercase tracking-wider"
                    >
                        <Plus size={12} /> Add Advance
                    </button>
                }
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-[var(--border)] font-mono-fin text-[10px] uppercase text-[var(--text-secondary)]">
                            <tr>
                                <th className="text-left py-2 px-2">Date</th>
                                <th className="text-left px-2">Client / Project</th>
                                <th className="text-left px-2">Mode / Ref</th>
                                <th className="text-right px-2">Amount</th>
                                <th className="text-right px-2">Adjusted</th>
                                <th className="text-right px-2">Outstanding</th>
                                <th className="text-center px-2">Status</th>
                                <th className="text-right px-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.entries || []).length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-[var(--text-secondary)] text-sm">
                                        No advances yet — add mobilisation or interim advances received from clients.
                                    </td>
                                </tr>
                            )}
                            {(data.entries || []).map((e) => (
                                <tr key={e.id} className="border-b border-[var(--border)]" data-testid={`adv-row-${e.id}`}>
                                    <td className="py-2 px-2 text-xs">{e.date}</td>
                                    <td className="px-2 text-xs">
                                        <div>{e.client_name}</div>
                                        <div className="text-[10px] text-[var(--text-secondary)]">{e.project_name || "—"}</div>
                                    </td>
                                    <td className="px-2 text-xs">
                                        <div>{e.mode}</div>
                                        <div className="text-[10px] font-mono-fin text-[var(--text-secondary)]">{e.reference_no || "—"}</div>
                                    </td>
                                    <td className="px-2 text-right text-xs font-bold">{fmt(e.amount)}</td>
                                    <td className="px-2 text-right text-xs">{fmt(e.adjusted_amount)}</td>
                                    <td className="px-2 text-right text-xs font-bold">{fmt(e.outstanding)}</td>
                                    <td className="px-2 text-center">
                                        <StatusPill status={e.status} />
                                    </td>
                                    <td className="px-2 text-right">
                                        {e.status !== "fully_adjusted" && (
                                            <button
                                                data-testid={`btn-adjust-${e.id}`}
                                                onClick={() => setAdjustTarget(e)}
                                                className="text-[10px] font-mono-fin text-[var(--brand-amber)] uppercase"
                                            >
                                                Adjust →
                                            </button>
                                        )}
                                        <button
                                            data-testid={`btn-delete-adv-${e.id}`}
                                            onClick={async () => {
                                                if (!window.confirm(`Delete advance ${e.id}?`)) return;
                                                await api.delete(`/advances/entries/${e.id}`);
                                                toast.success("Deleted");
                                                reload();
                                            }}
                                            className="ml-2 text-[var(--neg)]"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            {showForm && (
                <AdvanceForm
                    onClose={() => setShowForm(false)}
                    onSaved={() => {
                        setShowForm(false);
                        reload();
                    }}
                    clients={clients}
                    projects={projects}
                />
            )}
            {adjustTarget && (
                <AdjustAdvanceModal
                    entry={adjustTarget}
                    onClose={() => setAdjustTarget(null)}
                    onDone={() => {
                        setAdjustTarget(null);
                        reload();
                    }}
                />
            )}
        </div>
    );
}

function AdvanceForm({ onClose, onSaved, clients, projects }) {
    const [f, setF] = useState({
        client_id: "",
        project_id: "",
        date: new Date().toISOString().slice(0, 10),
        amount: "",
        mode: "RTGS",
        reference_no: "",
        purpose: "Mobilisation Advance",
        notes: "",
    });
    const filteredProjects = projects.filter((p) => !f.client_id || p.client_id === f.client_id);

    const submit = async () => {
        if (!f.client_id || !f.amount) return toast.error("Client and Amount required");
        try {
            await api.post("/advances/entries", {
                ...f,
                amount: Number(f.amount),
                project_id: f.project_id || null,
            });
            toast.success("Advance recorded");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    return (
        <ModalShell title="Add Client Advance" onClose={onClose} testId="modal-add-advance">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Client">
                    <select data-testid="input-adv-client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value, project_id: "" })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm">
                        <option value="">-- select --</option>
                        {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                    </select>
                </Field>
                <Field label="Project (optional)">
                    <select data-testid="input-adv-project" value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm">
                        <option value="">--</option>
                        {filteredProjects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                    </select>
                </Field>
                <Field label="Date">
                    <input data-testid="input-adv-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Amount (₹)">
                    <input data-testid="input-adv-amount" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-bold" />
                </Field>
                <Field label="Mode">
                    <select data-testid="input-adv-mode" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm">
                        {["Cheque", "RTGS", "NEFT", "IMPS", "UPI", "Cash", "Other"].map((m) => (<option key={m} value={m}>{m}</option>))}
                    </select>
                </Field>
                <Field label="Reference No.">
                    <input data-testid="input-adv-ref" value={f.reference_no} onChange={(e) => setF({ ...f, reference_no: e.target.value })} placeholder="UTR / cheque no." className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Purpose" className="col-span-2">
                    <input data-testid="input-adv-purpose" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Notes" className="col-span-2">
                    <input data-testid="input-adv-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                <button data-testid="btn-save-advance" onClick={submit} className="px-4 py-1.5 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold">
                    Save Advance
                </button>
            </div>
        </ModalShell>
    );
}

function AdjustAdvanceModal({ entry, onClose, onDone }) {
    const remaining = Number(entry.outstanding || 0);
    const [f, setF] = useState({
        adjust_amount: remaining,
        against_ra_bill_no: "",
        date: new Date().toISOString().slice(0, 10),
        notes: "",
    });
    const submit = async () => {
        if (!f.adjust_amount || Number(f.adjust_amount) <= 0) return toast.error("Amount must be > 0");
        try {
            await api.post(`/advances/entries/${entry.id}/adjust`, {
                ...f,
                adjust_amount: Number(f.adjust_amount),
            });
            toast.success("Advance adjusted");
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };
    return (
        <ModalShell title={`Adjust Advance · Outstanding ${fmt(remaining)}`} onClose={onClose} testId="modal-adjust-advance">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Adjust Amount (₹)">
                    <input data-testid="input-adjust-amount" type="number" value={f.adjust_amount} onChange={(e) => setF({ ...f, adjust_amount: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-bold" />
                </Field>
                <Field label="Against RA Bill No.">
                    <input data-testid="input-adjust-ra" value={f.against_ra_bill_no} onChange={(e) => setF({ ...f, against_ra_bill_no: e.target.value })} placeholder="RA-02" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Date">
                    <input data-testid="input-adjust-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
                <Field label="Notes" className="col-span-2">
                    <input data-testid="input-adjust-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm" />
                </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                <button data-testid="btn-confirm-adjust" onClick={submit} className="px-4 py-1.5 bg-[var(--pos)] text-black text-xs font-mono-fin uppercase font-bold">
                    Apply Adjustment
                </button>
            </div>
        </ModalShell>
    );
}

// ============ SHARED ============
function Kpi({ label, value, tone, testId }) {
    const color = tone === "pos" ? "text-[var(--pos)]" : tone === "warn" ? "text-[var(--brand-amber)]" : tone === "neg" ? "text-[var(--neg)]" : "text-[var(--text-primary)]";
    return (
        <div className="p-3 border border-[var(--border)] bg-[var(--surface)]" data-testid={testId}>
            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">{label}</div>
            <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: "bg-[var(--brand-amber)]/20 text-[var(--brand-amber)]",
        deposited: "bg-[var(--pos)]/20 text-[var(--pos)]",
        reconciled: "bg-[var(--pos)]/20 text-[var(--pos)]",
        held: "bg-[var(--brand-amber)]/20 text-[var(--brand-amber)]",
        released: "bg-[var(--pos)]/20 text-[var(--pos)]",
        due: "bg-[var(--neg)]/20 text-[var(--neg)]",
        upcoming: "bg-[var(--text-secondary)]/20 text-[var(--text-secondary)]",
        unadjusted: "bg-[var(--brand-amber)]/20 text-[var(--brand-amber)]",
        partially_adjusted: "bg-[var(--brand-amber)]/20 text-[var(--brand-amber)]",
        fully_adjusted: "bg-[var(--pos)]/20 text-[var(--pos)]",
    };
    return (
        <span className={`px-2 py-0.5 text-[10px] font-mono-fin uppercase ${map[status] || "bg-[var(--surface)] text-[var(--text-secondary)]"}`}>
            {status?.replace(/_/g, " ")}
        </span>
    );
}

function Field({ label, children, className }) {
    return (
        <div className={className}>
            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">{label}</div>
            {children}
        </div>
    );
}

function ModalShell({ title, children, onClose, testId }) {
    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
            <div
                className="bg-[var(--panel)] border border-[var(--border)] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-start mb-4">
                    <h3 className="font-display text-lg font-bold">{title}</h3>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">✕</button>
                </div>
                {children}
            </div>
        </div>
    );
}
